import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Zap,
  ArrowUpRight,
  Gift,
  ExternalLink,
  AlertTriangle,
  Gauge,
  Info,
} from "lucide-react";
import type {
  CreditBalance,
  CreditTransaction,
  WorkspaceBillingStatus,
} from "@yagni/shared";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { Input } from "../ui/input.js";
import { Pane } from "../ui/pane.js";
import {
  ChapterSection,
  ChapterLede,
  ChapterCard,
  ChapterLoading,
} from "../settings/chapter.js";

/**
 * Pull the server's `{ error }` message off a non-OK billing response so the
 * user sees WHY a button did nothing (e.g. "Payment processing is not
 * configured. Contact support."). Falls back to a friendly generic when the
 * body isn't the expected JSON shape. Never throws — a failed parse yields the
 * fallback so the caller always has something to show.
 */
async function readBillingError(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error.trim()) return body.error;
  } catch {
    // Non-JSON / empty body — fall through to the generic message.
  }
  return fallback;
}

const BILLING_NETWORK_ERROR =
  "Something went wrong reaching billing. Check your connection and try again.";

// --- Billing Overview types ---

interface BillingOverviewData {
  status: WorkspaceBillingStatus;
  credits: CreditBalance;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  signupCreditGranted: boolean;
}

interface CreditsData {
  credits: CreditBalance;
  transactions: CreditTransaction[];
  total: number;
  /**
   * Complete per-day spend totals (credits), grouped server-side by the
   * workspace timezone and aggregated across ALL debits — not just the paged-in
   * window. `day` is `YYYY-MM-DD` in `timezone`. Optional so an older API
   * response (or a bundle deployed ahead of the backend) falls back to the
   * legacy client-side per-day sum.
   */
  dailySpend?: { day: string; credits: number }[];
  /** IANA timezone the feed groups days by (e.g. "America/Chicago"). */
  timezone?: string;
}

interface GuardrailsData {
  dailyCreditCeiling: number;        // user-facing credits
  dailyCreditsUsed: number;          // user-facing credits, 2dp
  dailyCostCeilingMillicents: number; // raw cost-millicents (kept for future precision needs)
  dailyTokenResetAt: string | null;
}

const DAILY_LIMIT_PRESETS = [
  { value: 5, label: "$5" },
  { value: 10, label: "$10" },
  { value: 25, label: "$25" },
  { value: 50, label: "$50" },
  { value: 100, label: "$100" },
];

function formatCreditsCompact(n: number): string {
  if (n >= 100) return `${Math.round(n)}`;
  if (n >= 10) return n.toFixed(0);
  if (Number.isInteger(n)) return `${n}`;
  return n.toFixed(2);
}

// --- Components ---

function formatCredits(n: number): string {
  if (n >= 100) return n.toFixed(0);
  if (n >= 1) return n.toFixed(1);
  return n.toFixed(2);
}

function CreditMeter({ credits, hasActiveSubscription }: { credits: CreditBalance; hasActiveSubscription: boolean }) {
  // Use the canonical `total` from the billing snapshot. This stays constant
  // across the period, whereas the old `included + purchased` math shrank as
  // starter credits were spent (purchased historically conflated packs +
  // signup remaining).
  const total = credits.total;
  // Consumed against the *current* total allocation. Derive this from
  // `total - remaining`, NOT from `credits.used`: `credits.used` is a
  // cumulative lifetime-spend counter that (for starter/gifted workspaces with
  // no monthly period reset) can far exceed the current allocation. Dividing it
  // by `total` pinned the bar to 100% red even when most of the balance was
  // still available. Deriving from `remaining` keeps the card internally
  // consistent (consumed + remaining === total) and mirrors the backend's own
  // `remaining / total` warning thresholds in `emitCreditThresholdEvents`.
  const consumed = Math.max(0, total - credits.remaining);
  const usedPercent = total > 0 ? Math.min(100, (consumed / total) * 100) : 0;
  const isWarning = usedPercent >= 80;
  const isDanger = usedPercent >= 95;

  return (
    <ChapterCard className="p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-[var(--text-secondary)]">Credits</span>
        <Gauge size={18} className="text-[var(--text-tertiary)]" />
      </div>
      <div className="text-3xl font-bold text-[var(--text-primary)] mb-1 tabular-nums">
        {formatCredits(credits.remaining)}
        <span className="text-lg font-normal text-[var(--text-tertiary)]"> / {formatCredits(total)}</span>
      </div>

      {/* Progress bar: neutral track; only the fill carries color */}
      <div className="mt-3 h-2 bg-[rgba(var(--ink-rgb),0.08)] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isDanger
              ? "bg-[var(--error)]"
              : isWarning
                ? "bg-[var(--warning)]"
                : "bg-[var(--accent)]"
          }`}
          style={{ width: `${usedPercent}%` }}
        />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-[var(--text-secondary)]">
        <span>{formatCredits(consumed)} used</span>
        <span>{formatCredits(credits.remaining)} remaining</span>
      </div>

      {/* Breakdown — show static allocations so the rows sum to `total`. */}
      <div className="mt-3 pt-3 border-t border-[var(--border-subtle)] space-y-1">
        <div className="flex justify-between text-xs text-[var(--text-secondary)]">
          <span>{hasActiveSubscription ? "Plan included" : "Starter credits"}</span>
          <span className="tabular-nums">
            {formatCredits(hasActiveSubscription ? credits.included : credits.starterGranted)}
          </span>
        </div>
        {credits.granted > 0 && (
          <div className="flex justify-between text-xs text-[var(--text-secondary)]">
            <span>Gifted</span>
            <span className="tabular-nums">{formatCredits(credits.granted)}</span>
          </div>
        )}
        {credits.purchasedPacks > 0 && (
          <div className="flex justify-between text-xs text-[var(--text-secondary)]">
            <span>Purchased</span>
            <span className="tabular-nums">{formatCredits(credits.purchasedPacks)}</span>
          </div>
        )}
      </div>

      {isWarning && (
        <div className={`mt-3 flex items-center gap-2 text-xs ${isDanger ? "text-[var(--error)]" : "text-[var(--warning)]"}`}>
          <AlertTriangle size={12} />
          <span>{isDanger ? "Credits almost exhausted" : "Credits running low"}</span>
        </div>
      )}
    </ChapterCard>
  );
}

function sanitizeDescription(tx: CreditTransaction): string {
  const text = tx.description ?? tx.category.replace(/_/g, " ");
  const llm = text.match(/^\s*(Partial\s+)?LLM:.*?(?:\(([\d,]+)\s+tokens?\))?\s*$/i);
  if (llm) {
    const prefix = llm[1] ? "Partial AI usage" : "AI usage";
    return llm[2] ? `${prefix} (${llm[2]} tokens)` : prefix;
  }
  return text;
}

function CreditTransactionsFeed({
  transactions,
  total,
  dailySpend,
  timezone,
  onLoadMore,
  loading,
}: {
  transactions: CreditTransaction[];
  total: number;
  dailySpend?: { day: string; credits: number }[];
  timezone?: string;
  onLoadMore: () => void;
  loading: boolean;
}) {
  if (transactions.length === 0) {
    return (
      <ChapterCard className="p-8 text-center">
        <Zap size={32} className="mx-auto text-[var(--text-tertiary)] mb-3" />
        <div className="text-sm text-[var(--text-secondary)]">
          No credit activity yet. Your agents' actions will appear here.
        </div>
      </ChapterCard>
    );
  }

  // Group by day. Within each day, collapse all spend (debits) into a single
  // "AI usage" total \u2014 customers don't need a line per model call \u2014 and keep
  // credit additions (grants, purchases, plan renewals) as their own lines.
  // Day key + label in the workspace timezone when the server provides one,
  // else the browser's local zone (legacy behavior). Intl with
  // `timeZone: undefined` resolves to the local zone, so both paths share one
  // code path.
  const dayLabelFmt = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone || undefined,
    weekday: "long",
    month: "short",
    day: "numeric",
  });
  // en-CA yields a stable YYYY-MM-DD key that lines up with the server's
  // dailySpend[].day (same zone) so we can look up the complete daily total.
  const dayKeyFmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone || undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  // Complete per-day spend (credits) from the server, independent of how many
  // transactions have been paged in, so this total never grows on "Show more".
  const completeSpendByDay = new Map(
    (dailySpend ?? []).map((d) => [d.day, d.credits]),
  );

  const dayOrder: string[] = [];
  const byDay = new Map<
    string,
    {
      label: string;
      clientSpendTotal: number;
      spendCount: number;
      additions: CreditTransaction[];
    }
  >();
  for (const tx of transactions) {
    const when = new Date(tx.createdAt);
    const key = dayKeyFmt.format(when);
    let bucket = byDay.get(key);
    if (!bucket) {
      bucket = {
        label: dayLabelFmt.format(when),
        clientSpendTotal: 0,
        spendCount: 0,
        additions: [],
      };
      byDay.set(key, bucket);
      dayOrder.push(key);
    }
    if (tx.amount < 0) {
      bucket.clientSpendTotal += tx.amount;
      bucket.spendCount += 1;
    } else if (tx.amount > 0) {
      bucket.additions.push(tx);
    }
    // amount === 0 is a no-op ledger event — don't surface it on the feed.
  }

  return (
    <ChapterCard className="overflow-hidden">
      <div className="divide-y divide-[var(--border-subtle)]">
        {dayOrder.map((key) => {
          const bucket = byDay.get(key)!;
          // Prefer the server's complete daily total; fall back to the
          // client-side sum of loaded debits when `dailySpend` is absent.
          const completeSpend = completeSpendByDay.get(key);
          const spendToShow =
            completeSpend !== undefined
              ? completeSpend
              : Math.abs(bucket.clientSpendTotal);
          const hasSpend =
            completeSpend !== undefined ? completeSpend > 0 : bucket.spendCount > 0;
          if (!hasSpend && bucket.additions.length === 0) return null;
          return (
            <div key={key}>
              <div className="px-6 py-2 border-b border-[var(--print-border)]">
                <span className="text-xs font-medium text-[var(--text-secondary)]">{bucket.label}</span>
              </div>
              {hasSpend && (
                <div className="px-6 py-3 flex items-center gap-3 hover:bg-[rgba(var(--glass-rgb),0.55)]">
                  <div className="shrink-0">
                    <Zap size={14} className="text-[var(--accent)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--text-primary)]">AI usage</div>
                  </div>
                  <div className="text-sm font-medium font-mono tabular-nums text-[var(--text-secondary)]">
                    {"\u2212"}
                    {formatCredits(spendToShow)}
                  </div>
                </div>
              )}
              {bucket.additions.map((tx) => (
                <div
                  key={tx.id}
                  className="px-6 py-3 flex items-start gap-3 hover:bg-[rgba(var(--glass-rgb),0.55)]"
                >
                  <div className="mt-0.5">
                    {tx.category === "grant" ? (
                      <Gift size={14} className="text-[var(--success)]" />
                    ) : (
                      <ArrowUpRight size={14} className="text-[var(--success)]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--text-primary)] truncate">
                      {sanitizeDescription(tx)}
                    </div>
                    <div className="text-xs font-mono text-[var(--text-tertiary)] mt-0.5">
                      {new Date(tx.createdAt).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <div className="text-sm font-medium font-mono tabular-nums text-[var(--success)]">
                    +{formatCredits(Math.abs(tx.amount))}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {transactions.length < total && (
        <div className="px-6 py-3 border-t border-[var(--border-subtle)]">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onLoadMore}
            disabled={loading}
            className="text-[var(--accent)] hover:text-[var(--accent-hover)] px-0 hover:bg-transparent"
          >
            {loading ? "Loading..." : `Show more (${total - transactions.length} remaining)`}
          </Button>
        </div>
      )}
    </ChapterCard>
  );
}


function DailyCreditLimitCard({
  guardrails,
  onSave,
  saving,
  error,
}: {
  guardrails: GuardrailsData;
  onSave: (nextCeilingCredits: number) => Promise<void>;
  saving: boolean;
  error: string | null;
}) {
  const { dailyCreditCeiling, dailyCreditsUsed, dailyTokenResetAt } = guardrails;
  const usedPercent = dailyCreditCeiling > 0
    ? Math.min(100, (dailyCreditsUsed / dailyCreditCeiling) * 100)
    : 0;
  const isWarning = usedPercent >= 80;
  const isDanger = usedPercent >= 95;
  const atLimit = dailyCreditCeiling > 0 && dailyCreditsUsed >= dailyCreditCeiling;

  const [draft, setDraft] = useState<string>(String(dailyCreditCeiling));
  const previousCeilingRef = useRef(dailyCreditCeiling);
  useEffect(() => {
    if (previousCeilingRef.current === dailyCreditCeiling) return;
    previousCeilingRef.current = dailyCreditCeiling;
    setDraft(String(dailyCreditCeiling));
  }, [dailyCreditCeiling]);

  const parsedDraft = Number(draft);
  const draftIsValid = Number.isFinite(parsedDraft) && parsedDraft >= 1;
  const draftChanged = draftIsValid && parsedDraft !== dailyCreditCeiling;

  const resetsAt = (() => {
    const base = dailyTokenResetAt ? new Date(dailyTokenResetAt) : new Date();
    const next = new Date(Date.UTC(
      base.getUTCFullYear(),
      base.getUTCMonth(),
      base.getUTCDate() + 1,
      0, 0, 0,
    ));
    return next.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  })();

  return (
    <div>
      <ChapterSection
        label="Daily credit limit"
        rightLabel={`Resets ${resetsAt}`}
      />
      <ChapterLede>
        A safety ceiling that caps how many credits this workspace can spend in
        a single day. It protects you from a runaway agent. Raise it anytime;
        it resets at midnight UTC.
      </ChapterLede>
      {/* Material = commitment state: a changed, unsaved ceiling is staged
          (glass) until Save prints it back into the record. */}
      <Pane material={draftChanged ? "staged" : "printed"} className="p-6">

        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm text-[var(--text-secondary)]">Today</span>
          <span className="text-sm tabular-nums text-[var(--text-primary)] font-medium">
            {formatCreditsCompact(dailyCreditsUsed)} / {formatCreditsCompact(dailyCreditCeiling)} credits
          </span>
        </div>
        <div className="h-2 bg-[rgba(var(--ink-rgb),0.08)] rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isDanger
                ? "bg-[var(--error)]"
                : isWarning
                  ? "bg-[var(--warning)]"
                  : "bg-[var(--accent)]"
            }`}
            style={{ width: `${usedPercent}%` }}
          />
        </div>

        {atLimit && (
          <div className="mt-3 flex items-center gap-2 text-xs text-[var(--error)]">
            <AlertTriangle size={12} />
            Agents are paused until the ceiling is raised or tomorrow's reset.
          </div>
        )}

        <div className="mt-5">
          <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">
            New daily ceiling
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {DAILY_LIMIT_PRESETS.map((preset) => {
              const active = parsedDraft === preset.value;
              return (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setDraft(String(preset.value))}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    active
                      ? "border-[var(--accent)] bg-[var(--accent-muted)] text-[var(--accent)]"
                      : "border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Input
              type="number"
              inputMode="decimal"
              min={1}
              step={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="flex-1 tabular-nums"
              inputSize="sm"
              placeholder="e.g. 25"
              aria-label="Custom daily credit ceiling"
            />
            <Button
              type="button"
              size="sm"
              onClick={() => void onSave(parsedDraft)}
              disabled={!draftChanged || saving}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
          {!draftIsValid && (
            <div className="mt-2 text-xs text-[var(--error)]">Enter a number ≥ 1.</div>
          )}
          {error && (
            <div className="mt-2 text-xs text-[var(--error)]">{error}</div>
          )}
        </div>
      </Pane>
    </div>
  );
}

/**
 * YAGNI sells no plans, packs or hosting in the app: the plan is agreed with
 * YAGNI and invoiced by hand. This card says so and points at Usage.
 */
function ManagedPlanNotice({ status }: { status: WorkspaceBillingStatus | null }) {
  return (
    <ChapterCard className="mb-6 p-6">
      <div className="flex items-start gap-3">
        <Info size={16} className="mt-0.5 shrink-0 text-[var(--accent)]" />
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
            Your plan is managed by YAGNI
          </h3>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            YAGNI invoices your plan directly. Usage and credits are below, and{" "}
            <Link to="/usage" className="text-[var(--accent)] underline-offset-2 hover:underline">
              Usage
            </Link>{" "}
            has the full breakdown. To change your plan or add credits, talk to us.
          </p>
          {status === "past_due" && (
            <div className="mt-3 flex items-center gap-2 text-xs text-[var(--warning)]">
              <AlertTriangle size={12} />
              <span>A payment is past due. Open invoices to settle it.</span>
            </div>
          )}
        </div>
      </div>
    </ChapterCard>
  );
}

// --- Main View ---

export function BillingView() {
  const [overview, setOverview] = useState<BillingOverviewData | null>(null);
  const [creditsData, setCreditsData] = useState<CreditsData | null>(null);
  const [guardrails, setGuardrails] = useState<GuardrailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [creditsTxLoading, setCreditsTxLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [dailyLimitSaving, setDailyLimitSaving] = useState(false);
  const [dailyLimitError, setDailyLimitError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [overviewRes, creditsRes, guardrailsRes] =
        await Promise.all([
          fetch("/api/billing/overview"),
          fetch("/api/billing/credits?limit=50"),
          fetch("/api/settings/guardrails"),
        ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (creditsRes.ok) setCreditsData(await creditsRes.json());
      if (guardrailsRes.ok) {
        const body = (await guardrailsRes.json()) as {
          dailyCreditCeiling: number;
          dailyCreditsUsed: number;
          dailyCostCeilingMillicents: number;
          dailyTokenResetAt: string | null;
        };
        setGuardrails({
          dailyCreditCeiling: body.dailyCreditCeiling,
          dailyCreditsUsed: body.dailyCreditsUsed,
          dailyCostCeilingMillicents: body.dailyCostCeilingMillicents,
          dailyTokenResetAt: body.dailyTokenResetAt,
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSaveDailyLimit = useCallback(async (nextCeilingCredits: number) => {
    setDailyLimitError(null);
    setDailyLimitSaving(true);
    try {
      const res = await fetch("/api/settings/guardrails", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dailyCreditCeiling: nextCeilingCredits }),
      });
      if (res.ok) {
        const body = (await res.json()) as {
          dailyCreditCeiling: number;
          dailyCreditsUsed: number;
          dailyCostCeilingMillicents: number;
          dailyTokenResetAt: string | null;
        };
        setGuardrails({
          dailyCreditCeiling: body.dailyCreditCeiling,
          dailyCreditsUsed: body.dailyCreditsUsed,
          dailyCostCeilingMillicents: body.dailyCostCeilingMillicents,
          dailyTokenResetAt: body.dailyTokenResetAt,
        });
      } else {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        setDailyLimitError(err.error ?? "Could not update the daily limit. Try again?");
      }
    } catch {
      setDailyLimitError("Could not update the daily limit. Try again?");
    } finally {
      setDailyLimitSaving(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Bring the message into view when a billing action fails, so a portal error
  // scrolled off-screen never reads as "nothing happened".
  useEffect(() => {
    if (actionError) {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [actionError]);

  const handleManageBilling = async () => {
    setActionError(null);
    setActionLoading(true);
    try {
      const res = await fetch("/api/billing/portal", { method: "POST" });
      if (res.ok) {
        const { url } = await res.json();
        if (url) {
          window.location.href = url;
          return;
        }
        setActionError(
          "We couldn't open the billing portal. Please try again, or contact support.",
        );
      } else {
        setActionError(
          await readBillingError(
            res,
            "We couldn't open the billing portal. Please try again, or contact support.",
          ),
        );
      }
    } catch {
      setActionError(BILLING_NETWORK_ERROR);
    } finally {
      setActionLoading(false);
    }
  };

  const handleLoadMoreCredits = async () => {
    if (!creditsData || creditsTxLoading) return;
    setCreditsTxLoading(true);
    try {
      const res = await fetch(
        `/api/billing/credits?limit=50&offset=${creditsData.transactions.length}`
      );
      if (res.ok) {
        const more: CreditsData = await res.json();
        setCreditsData({
          ...more,
          transactions: [...creditsData.transactions, ...more.transactions],
        });
      }
    } finally {
      setCreditsTxLoading(false);
    }
  };

  if (loading) {
    return <ChapterLoading />;
  }

  const hasActiveSubscription = overview?.currentPeriodStart != null;

  return (
    <div>
      <ManagedPlanNotice status={overview?.status ?? null} />

      {overview && (
        <>
          <ChapterSection label="Balance" />
          <CreditMeter
            credits={overview.credits}
            hasActiveSubscription={hasActiveSubscription}
          />
        </>
      )}

      {/* Daily credit limit: the safety ceiling agents hit before credits
          are depleted. Renders its own section rule. */}
      {guardrails && (
        <DailyCreditLimitCard
          guardrails={guardrails}
          onSave={handleSaveDailyLimit}
          saving={dailyLimitSaving}
          error={dailyLimitError}
        />
      )}

      {/* Invoices and receipts live in the Stripe billing portal. */}
      <ChapterSection label="Invoices" />
      <div className="space-y-4">
        {actionError && (
          <div ref={errorRef}>
            <Card tone="warning" padding="sm" className="text-sm">
              {actionError}
            </Card>
          </div>
        )}

        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleManageBilling}
          disabled={actionLoading}
        >
          <ExternalLink size={14} />
          Invoices and receipts
        </Button>
      </div>

      {/* Credit transactions */}
      {creditsData && (
        <>
          <ChapterSection label="Credit activity" />
          <CreditTransactionsFeed
            transactions={creditsData.transactions}
            total={creditsData.total}
            dailySpend={creditsData.dailySpend}
            timezone={creditsData.timezone}
            onLoadMore={handleLoadMoreCredits}
            loading={creditsTxLoading}
          />
        </>
      )}
    </div>
  );
}
